import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

const CATEGORIES = ["All", "Handloom", "Spices", "Pottery", "Ayurveda", "Jewellery", "Home Decor"];

export default function Products() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smart, setSmart] = useState(params.get("mode") !== "keyword");
  const [interpreted, setInterpreted] = useState("");
  const category = params.get("category") || "All";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setInterpreted("");
      try {
        if (smart && q) {
          const { data } = await api.post("/products/smart-search", { query: q, limit: 24 });
          if (cancelled) return;
          const list = category !== "All"
            ? data.products.filter((p) => p.category === category)
            : data.products;
          setProducts(list);
          setInterpreted(data.interpreted || "");
        } else {
          const p = {};
          if (category !== "All") p.category = category;
          if (q) p.q = q;
          const { data } = await api.get("/products", { params: p });
          if (!cancelled) setProducts(data);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [category, q, smart]);

  const setCat = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search) next.set("q", search); else next.delete("q");
    setParams(next);
  };

  const toggleSmart = () => {
    const nv = !smart;
    setSmart(nv);
    const next = new URLSearchParams(params);
    if (nv) next.delete("mode"); else next.set("mode", "keyword");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="label-caps mb-2">{t("Bazaar")}</div>
        <h1 className="text-4xl md:text-5xl">{t("Discover Swadeshi treasures")}</h1>
      </div>

      <form onSubmit={submitSearch} className="mb-3 max-w-2xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#718096]" />
          <input
            data-testid="products-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={smart ? t("Try 'haldi', 'mitti ka bartan', 'wedding saree'…") : t("Search products…")}
            className="w-full pl-10 pr-32 py-3 rounded-full bg-white border border-[#E2DCD0] focus:outline-none focus:border-[#E25A24] text-sm"
          />
          <button
            type="button"
            data-testid="smart-search-toggle"
            onClick={toggleSmart}
            title={smart ? "AI semantic search ON" : "Keyword search"}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              smart ? "bg-[#E25A24] text-white" : "bg-[#F2EFEB] text-[#4A5568]"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" /> AI
          </button>
        </div>
      </form>

      {smart && q && interpreted && !loading && (
        <div className="mb-6 max-w-2xl text-xs text-[#718096]">
          <span className="label-caps">{t("Interpreted as")}</span> <span className="italic">"{interpreted}"</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        <Link
          to="/genz"
          data-testid="filter-genz"
          className="px-4 py-2 rounded-full text-sm font-bold border bg-[#0D141C] text-[#FFB07C] border-[#0D141C] hover:bg-[#13294B] transition-colors inline-flex items-center gap-1.5"
        >
          🔥 Gen Z
        </Link>
        <Link
          to="/genalpha"
          data-testid="filter-genalpha"
          className="px-4 py-2 rounded-full text-sm font-bold border bg-[#13294B] text-[#FFDDC5] border-[#13294B] hover:bg-[#0D141C] transition-colors inline-flex items-center gap-1.5"
        >
          🧸 Gen Alpha
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#718096]"><Loader2 className="w-4 h-4 animate-spin" /> {smart && q ? t("Bazaari is searching…") : t("Loading…")}</div>
      ) : products.length === 0 ? (
        <div className="py-24 text-center">
          <div className="font-serif text-2xl text-[#13294B]">{t("No products found")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
